import React, { useState } from 'react';
import type { DefaultContainerProps } from '@wix/editor-elements-types/thunderbolt';
import { useDidUpdate } from '@wix/thunderbolt-elements/src/providers/useDidUpdate';

const HiddenState: React.FC<{
  id?: string;
  children?: React.ReactNode;
}> = ({ children, id }) => (
  <span style={{ display: 'none' }} id={id}>
    {children}
  </span>
);

export const useMSBStates = ({
  children,
  getStateId,
  selectedStateId,
  onChange,
  shouldRenderAllTabs = false,
  wrap = (arg: any) => arg,
}: {
  children: React.ReactNode | DefaultContainerProps['children'];
  getStateId: (state: React.ReactElement) => string;
  selectedStateId: string;
  onChange?: (event: React.ChangeEvent) => void;
  shouldRenderAllTabs?: boolean;
  wrap?: (tabElement: any) => React.ReactNode;
}) => {
  /**
   * We keep state memory to preserve DOM state (like inputs)
   */
  const [pastStateIds, setPastStateIds] = useState<Array<string>>([]);

  useDidUpdate(() => {
    onChange?.({ type: 'change' } as React.ChangeEvent);

    if (!shouldRenderAllTabs && !pastStateIds.includes(selectedStateId)) {
      setPastStateIds([...pastStateIds, selectedStateId]);
    }
  }, [selectedStateId]);

  /**
   * TB Containers pass children as a function
   */
  const stateElements = React.Children.toArray(
    typeof children === 'function' ? (children as Function)() : children,
  );

  if (shouldRenderAllTabs) {
    return stateElements.map(wrap);
  }

  const currentOrPastStates = stateElements.filter(state => {
    if (!React.isValidElement(state)) {
      return false;
    }
    const itemId = getStateId(state);
    return selectedStateId === itemId || pastStateIds.includes(itemId);
  }) as Array<React.ReactElement>;

  if (currentOrPastStates.length) {
    return currentOrPastStates.map(state =>
      selectedStateId === getStateId(state) ? (
        wrap(state)
      ) : (
        // If we rendered this state in the past - render it hidden, to maintain DOM state
        <HiddenState id={state.props.id}>{wrap(state)}</HiddenState>
      ),
    );
  }

  return stateElements.slice(0, 1).map(wrap);
};
